import React, { useEffect, useState } from "react";
import {
  deleteAccount,
  exportAccountCsvUrl,
  fetchAiConsent,
  setAiConsent,
} from "../lib/api";

/**
 * Profile → PRIVACY section (Batch B / GDPR).
 *
 * Three controls bundled together so the user has a single place
 * to action every privacy right we offer:
 *
 *   1. AI consent toggle. OFF blocks every `/api/ai/*` route until
 *      flipped ON (the consent version is stamped on grant).
 *   2. Download my data. JSON (in-page) + CSV ZIP (direct link).
 *   3. Delete my account — two modes (anonymize / hard), each
 *      requiring the user to type the literal word "DELETE" before
 *      the button enables.
 *
 * Toggle visibility on the parent <ProfilePanel> via the GLORY-style
 * `defaultExpanded` mechanism — this file just renders the contents.
 */
export default function PrivacySection({ onAccountDeleted }) {
  const [consent, setConsent] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [mode, setMode] = useState("anonymize");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchAiConsent().then(setConsent).catch(() => setConsent({ granted: false }));
  }, []);

  const onToggleConsent = async () => {
    setBusy(true); setErr("");
    try {
      const next = await setAiConsent(!consent?.granted);
      setConsent(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not update AI consent.");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true); setErr("");
    try {
      await deleteAccount(mode);
      if (typeof onAccountDeleted === "function") {
        onAccountDeleted(mode);
      } else {
        window.location.assign("/");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Account deletion failed.");
    } finally {
      setBusy(false);
    }
  };

  const canDelete = confirmText.trim() === "DELETE" && !busy;

  return (
    <div data-testid="profile-privacy-section" className="space-y-6">
      {/* ── AI consent ─────────────────────────────────────────── */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <p className="text-[12px] tracking-widest text-white/90 font-bold">
              AI PROCESSING CONSENT
            </p>
            <p className="mt-1 text-[11px] text-white/60 leading-snug">
              LEAGUE HUB sends your text/image prompts to third-party
              AI providers (DeepSeek, OpenAI, Gemini) when you use
              avatar generation, league banners, or coaching tips.
              You must grant explicit consent before any AI feature
              works — and you can revoke it at any time.
            </p>
            {consent?.granted_at && (
              <p className="mt-2 text-[10px] text-white/40">
                Granted: {new Date(consent.granted_at).toLocaleString()}
                {" · "}Version: {consent.version}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onToggleConsent}
            disabled={busy}
            data-testid="privacy-ai-consent-toggle"
            className={[
              "px-3 py-1.5 text-[10px] tracking-widest rounded-lg font-bold transition",
              consent?.granted
                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40"
                : "bg-white/8 text-white border border-white/20 hover:bg-white/15",
              busy && "opacity-50 cursor-wait",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {consent?.granted ? "ON · TAP TO REVOKE" : "OFF · GRANT"}
          </button>
        </div>
      </div>

      {/* ── Data export ────────────────────────────────────────── */}
      <div className="rounded-xl border border-white/10 bg-black/30 p-4">
        <p className="text-[12px] tracking-widest text-white/90 font-bold">
          DOWNLOAD MY DATA
        </p>
        <p className="mt-1 text-[11px] text-white/60 leading-snug">
          Export every row stored under your account across 24+
          collections (users, sessions, leagues, teams, DMs, reports,
          payments, …). JSON for machine reading, ZIP-of-CSVs for
          spreadsheets.
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-[10px] tracking-widest">
          <a
            href={exportAccountCsvUrl()}
            data-testid="privacy-export-csv"
            className="px-3 py-1.5 rounded-lg bg-white/8 text-white hover:bg-white/15 border border-white/15"
            target="_blank"
            rel="noopener noreferrer"
          >
            DOWNLOAD CSV ZIP
          </a>
          <a
            href={exportAccountCsvUrl().replace("?fmt=csv", "")}
            data-testid="privacy-export-json"
            className="px-3 py-1.5 rounded-lg bg-white/8 text-white hover:bg-white/15 border border-white/15"
            target="_blank"
            rel="noopener noreferrer"
          >
            DOWNLOAD JSON
          </a>
        </div>
      </div>

      {/* ── Delete account ─────────────────────────────────────── */}
      <div
        className="rounded-xl border border-red-500/40 bg-red-500/5 p-4"
        data-testid="privacy-delete-section"
      >
        <p className="text-[12px] tracking-widest text-red-300 font-bold">
          DELETE MY ACCOUNT · DANGER ZONE
        </p>
        <p className="mt-1 text-[11px] text-white/65 leading-snug">
          Choose how you want to leave LEAGUE HUB. Both modes
          immediately terminate your sessions and transfer any leagues
          / teams you own to the next member (or dissolve them if
          you're solo).
        </p>
        <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[11px]">
          <label className={`rounded-lg border p-3 cursor-pointer ${mode === "anonymize" ? "border-emerald-400/50 bg-emerald-500/10" : "border-white/10 hover:border-white/30"}`}>
            <input
              type="radio"
              name="del-mode"
              value="anonymize"
              checked={mode === "anonymize"}
              onChange={() => setMode("anonymize")}
              data-testid="privacy-mode-anonymize"
              className="mr-2 accent-emerald-400"
            />
            <span className="font-bold tracking-widest text-[10px]">ANONYMIZE</span>
            <p className="mt-1 text-white/55">
              Keep your authored content visible but scrub PII
              (name → "[Deleted User]", email cleared, sessions
              dropped). Recommended for GDPR-conscious users who
              want match history preserved.
            </p>
          </label>
          <label className={`rounded-lg border p-3 cursor-pointer ${mode === "hard" ? "border-red-400/50 bg-red-500/10" : "border-white/10 hover:border-white/30"}`}>
            <input
              type="radio"
              name="del-mode"
              value="hard"
              checked={mode === "hard"}
              onChange={() => setMode("hard")}
              data-testid="privacy-mode-hard"
              className="mr-2 accent-red-400"
            />
            <span className="font-bold tracking-widest text-[10px]">FULL ERASURE</span>
            <p className="mt-1 text-white/55">
              Hard-delete your user row + every PII-bearing record we
              hold. Authored leaderboard/match rows are reassigned to
              a synthetic "deleted_user" placeholder.
            </p>
          </label>
        </div>
        <p className="mt-3 text-[11px] text-white/60">
          Type <span className="text-red-300 font-mono font-bold">DELETE</span> to
          confirm:
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          data-testid="privacy-delete-confirm-input"
          className="mt-1 w-full sm:w-64 px-3 py-1.5 text-[11px] rounded-lg bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:border-red-400/50 focus:outline-none"
        />
        <div className="mt-3">
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete}
            data-testid="privacy-delete-submit"
            className={[
              "px-3 py-1.5 text-[10px] tracking-widest rounded-lg font-bold transition",
              canDelete
                ? "bg-red-500 text-black hover:bg-red-400"
                : "bg-white/8 text-white/30 cursor-not-allowed",
            ].join(" ")}
          >
            {busy ? "WORKING…" : `DELETE MY ACCOUNT (${mode.toUpperCase()})`}
          </button>
        </div>
      </div>

      {err && (
        <p
          data-testid="privacy-error"
          className="text-[11px] text-red-300 bg-red-500/10 border border-red-400/40 rounded-lg p-2"
        >
          {err}
        </p>
      )}
    </div>
  );
}
